import React, { useEffect, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { AnimatePresence, motion } from "framer-motion";
import { Users, Loader2, Plus, X, ShieldCheck, UsersRound, Compass, MessageSquareText, Crown, Sparkles, CalendarDays, Video, MapPin, Trash2, ExternalLink, Check, Clock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PostCard, PostForm } from "@/pages/Showcase";
import { CheckinBar } from "@/components/CheckinBar";
import { StreakLeaderboard } from "@/components/StreakLeaderboard";
import { WeeklyAwards } from "@/components/WeeklyAwards";
import { NearbyMembers } from "@/components/NearbyMembers";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "professional", label: "Professional" },
  { id: "business", label: "Business" },
  { id: "talent", label: "Talent" },
];

const COMMUNITY_CATS = ["all", "Interest", "Geography", "Topic", "Politics", "Sport", "Profession", "Cause", "Hobby", "Technology", "Local", "Education", "Faith"];

export default function CommunityHub() {
  const { user } = useApp();
  const [communities, setCommunities] = useState([]);
  const [myCommunity, setMyCommunity] = useState(null);
  const [selected, setSelected] = useState(null); // null = main feed, else community object
  const [cat, setCat] = useState("all");
  const [commCat, setCommCat] = useState("all"); // community category filter
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const loadCommunities = async () => {
    try {
      const q = commCat !== "all" ? `?category=${encodeURIComponent(commCat)}` : "";
      const { data } = await api.get(`/communities${q}`);
      setCommunities(data.communities || []);
      setMyCommunity(data.my_community || null);
    } catch (e) { /* silent */ }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const params = selected ? `?community_id=${selected.id}` : (cat !== "all" ? `?category=${cat}` : "");
      const { data } = await api.get(`/showcase${params}`);
      setPosts(data.posts || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCommunities(); /* eslint-disable-next-line */ }, [commCat]);
  useEffect(() => { loadPosts(); /* eslint-disable-next-line */ }, [selected, cat]);

  const join = async (c) => {
    if (!user) { toast.error("Sign in to join a Kind Community."); return; }
    try {
      if (c.join_mode === "follow") {
        const { data } = await api.post(`/communities/${c.id}/follow`);
        toast.success(data.following ? `Following ${c.name}` : `Unfollowed ${c.name}`);
      } else {
        const { data } = await api.post(`/communities/${c.id}/join`);
        const msg = { joined: `Joined ${c.name}`, left: `Left ${c.name}`, requested: `Request sent — ${c.owner_name} will approve you 🙏`, cancelled: "Request cancelled" }[data.status] || "Updated";
        toast.success(msg);
      }
      loadCommunities();
      if (selected?.id === c.id) { const { data: d } = await api.get(`/communities/${c.id}`); setSelected(d.community); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6" data-testid="community-hub">
      <div className="flex items-center gap-2 rounded-xl bg-brand/10 border border-brand/30 p-3 text-xs">
        <ShieldCheck size={16} className="text-brand shrink-0" />
        <span className="text-[var(--rud-text)]">A kind, safe space. Others see only your public profile — name, city, gender. We never share your spending, health, address, DoB or ID. Just your posts — text, videos, links, images. Everyone is one family.</span>
      </div>

      {/* Communities strip */}
      <div className="flex items-center justify-between mt-6 mb-3">
        <h3 className="font-serif text-2xl flex items-center gap-2"><Compass size={18} className="text-brand" /> Kind Communities</h3>
        {user && !myCommunity && (
          <button data-testid="create-community-btn" onClick={() => setShowCreate(true)} className="btn-primary h-9 px-4 flex items-center gap-1.5 text-sm"><Plus size={15} /> Create your Kind Community</button>
        )}
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-1" data-testid="community-cat-filters">
        {COMMUNITY_CATS.map((cc) => (
          <button key={cc} data-testid={`comm-catfilter-${cc.toLowerCase()}`} onClick={() => setCommCat(cc)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs transition-colors border ${commCat === cc ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand/60"}`}>
            {cc === "all" ? "All" : cc}
          </button>
        ))}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="communities-list">
        <button
          data-testid="community-chip-feed"
          onClick={() => setSelected(null)}
          className={`shrink-0 w-52 text-left rounded-2xl border p-4 transition-colors ${!selected ? "border-brand bg-brand/10" : "border-rud bg-elevated hover:border-brand/50"}`}
        >
          <MessageSquareText size={18} className="text-brand" />
          <div className="font-medium mt-2">Community Feed</div>
          <div className="text-[11px] text-sec mt-0.5">Everyone's posts</div>
        </button>
        {communities.map((c) => (
          <button
            key={c.id}
            data-testid={`community-chip-${c.id}`}
            onClick={() => setSelected(c)}
            className={`shrink-0 w-52 text-left rounded-2xl border p-4 transition-colors ${selected?.id === c.id ? "border-brand bg-brand/10" : "border-rud bg-elevated hover:border-brand/50"}`}
          >
            <div className="flex items-center justify-between">
              <UsersRound size={18} className="text-brand" />
              {c.is_owner && <Crown size={14} className="text-yellow-400" />}
            </div>
            <div className="font-medium mt-2 truncate">{c.name}</div>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {c.category && <span className="text-[10px] uppercase tracking-wide text-brand bg-brand/10 rounded px-1.5 py-0.5">{c.category}</span>}
              {c.interest && <span className="text-[11px] text-sec truncate">#{c.interest}</span>}
            </div>
            <div className="text-[11px] text-sec mt-1">{c.member_count} member{c.member_count === 1 ? "" : "s"} · {c.post_count} post{c.post_count === 1 ? "" : "s"}</div>
          </button>
        ))}
        {communities.length === 0 && (
          <div className="shrink-0 w-64 rounded-2xl border border-dashed border-rud p-4 text-xs text-sec grid place-items-center text-center">
            No communities yet. {user ? "Create the first one!" : "Sign in to create one."}
          </div>
        )}
      </div>

      {/* Selected community header / main feed categories */}
      {selected ? (
        <>
        <div className="rud-card rounded-2xl p-5 mt-5 flex flex-wrap items-center justify-between gap-3" data-testid="community-header">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-serif text-2xl">{selected.name}</h4>
              {selected.interest && <span className="text-xs text-brand border border-brand/40 rounded-full px-2 py-0.5">#{selected.interest}</span>}
            </div>
            {selected.description && <p className="text-sm text-sec mt-1 max-w-xl">{selected.description}</p>}
            <p className="text-[11px] text-sec mt-1">By {selected.owner_name} · {selected.member_count} members</p>
          </div>
          <div className="flex items-center gap-2">
            {!selected.is_owner && (
              selected.join_mode === "follow" ? (
                <button data-testid="community-follow-btn" onClick={() => join(selected)} className={`h-9 px-4 rounded-full text-sm border flex items-center gap-1.5 transition-colors ${selected.is_following ? "border-brand text-brand" : "border-rud text-sec hover:border-brand"}`}>
                  {selected.is_following ? "Following" : "Follow"}
                </button>
              ) : (
                <button data-testid="community-join-btn" onClick={() => join(selected)} className={`h-9 px-4 rounded-full text-sm border flex items-center gap-1.5 transition-colors ${selected.is_member ? "border-brand text-brand" : selected.is_pending ? "border-amber-400/50 text-amber-400" : "border-rud text-sec hover:border-brand"}`}>
                  {selected.is_member ? "Joined" : selected.is_pending ? "Requested" : (selected.join_mode === "approve" ? "Request to join" : "Join")}
                </button>
              )
            )}
            {user && (selected.is_member || selected.is_owner) && (
              <button data-testid="community-post-btn" onClick={() => setShowForm(true)} className="btn-primary h-9 px-4 flex items-center gap-1.5 text-sm"><Plus size={15} /> Post</button>
            )}
          </div>
        </div>
        <div className="rud-card rounded-2xl p-4 mt-3" data-testid="community-rules">
          <div className="flex items-center gap-2 text-brand text-xs uppercase tracking-widest mb-2"><ShieldCheck size={14} /> Kind Community Rules</div>
          <ul className="text-xs text-sec space-y-1 list-disc pl-4">
            {(selected.rules || []).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
          <p className="text-[11px] text-brand/80 mt-2">My Kind rewards kindness — hurtful, hateful, divisive or cribbing content quietly lowers your reach. Respectful feedback is always welcome. We are one family. 🌍</p>
        </div>
        {selected.is_owner && <CommunityRequests community={selected} onChange={() => { loadCommunities(); api.get(`/communities/${selected.id}`).then(({ data }) => setSelected(data.community)); }} />}
        <CommunityMeetups community={selected} user={user} />
        </>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
          <div className="flex gap-1 p-1 rounded-full bg-elevated border border-rud w-fit">
            {CATEGORIES.map((c) => (
              <button key={c.id} data-testid={`community-cat-${c.id}`} onClick={() => setCat(c.id)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${cat === c.id ? "bg-brand text-black" : "text-sec"}`}>{c.label}</button>
            ))}
          </div>
          {user && (
            <button data-testid="showcase-new" onClick={() => setShowForm(true)} className="btn-primary h-9 px-4 flex items-center gap-1.5 text-sm"><Plus size={15} /> Share your work</button>
          )}
        </div>
      )}

      {!user && (
        <div className="rud-card rounded-2xl p-6 mt-5 text-center">
          <p className="text-sm text-sec">Sign in to post, join communities, follow creators and comment.</p>
          <a href="/auth" className="btn-primary h-10 px-6 inline-flex items-center mt-3 text-sm">Sign in</a>
        </div>
      )}

      {user && !selected && <CheckinBar onPosted={loadPosts} />}
      {!selected && <WeeklyAwards />}
      {user && !selected && <StreakLeaderboard />}
      {user && !selected && <NearbyMembers />}

      {loading ? (
        <div className="mt-10 text-center text-sec"><HeartLoader /></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          {posts.map((p) => <PostCard key={p.id} post={p} me={user} onChange={loadPosts} />)}
          {posts.length === 0 && <div className="col-span-2 text-center text-sec text-sm py-10">{selected ? "No posts in this community yet. Be the first!" : "No posts yet. Be the first to share."}</div>}
        </div>
      )}

      <AnimatePresence>
        {showForm && <PostForm communityId={selected?.id || null} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); loadPosts(); loadCommunities(); }} />}
        {showCreate && <CreateCommunity onClose={() => setShowCreate(false)} onDone={(c) => { setShowCreate(false); loadCommunities(); setSelected(c); }} />}
      </AnimatePresence>
    </motion.div>
  );
}

function CommunityRequests({ community, onChange }) {
  const [reqs, setReqs] = useState([]);
  const load = async () => { try { const { data } = await api.get(`/communities/${community.id}/requests`); setReqs(data.requests || []); } catch { /* silent */ } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [community.id]);
  const act = async (uid, approve) => {
    try {
      await api.post(`/communities/${community.id}/${approve ? "approve" : "decline"}`, { user_id: uid });
      toast.success(approve ? "Approved 🎉 Welcome to the family" : "Request declined");
      load(); onChange?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (reqs.length === 0) return null;
  return (
    <div className="rud-card rounded-2xl p-4 mt-3" data-testid="community-requests">
      <div className="flex items-center gap-2 text-brand text-sm font-medium mb-2"><Clock size={15} /> Join requests ({reqs.length})</div>
      <div className="space-y-2">
        {reqs.map((r) => (
          <div key={r.user_id} data-testid={`request-${r.user_id}`} className="flex items-center gap-3 rounded-lg border border-rud bg-white/5 px-3 py-2">
            <div className="flex-1 min-w-0 text-sm truncate">{r.name}</div>
            <button data-testid={`request-approve-${r.user_id}`} onClick={() => act(r.user_id, true)} className="btn-primary rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"><Check size={13} /> Approve</button>
            <button data-testid={`request-decline-${r.user_id}`} onClick={() => act(r.user_id, false)} className="text-sec hover:text-rose-400 transition-colors"><X size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CommunityMeetups({ community, user }) {
  const [meetups, setMeetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const canParticipate = user && (community.is_member || community.is_owner);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/communities/${community.id}/meetups`); setMeetups(data.meetups || []); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [community.id]);

  const openMeet = (m) => {
    const url = m.link || (m.secure_room ? `${window.location.origin}/meet?room=${encodeURIComponent(m.secure_room)}` : "");
    if (url) window.open(url, "_blank", "noopener");
    else toast.error("No meeting link yet.");
  };

  const rsvp = async (m) => {
    if (!user) { toast.error("Sign in to RSVP."); return; }
    setBusy(m.id);
    try {
      const { data } = await api.post(`/communities/meetups/${m.id}/rsvp`);
      toast.success(data.going ? "You're going 🎉" : "RSVP removed");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const remove = async (m) => {
    setBusy(m.id);
    try { await api.delete(`/communities/meetups/${m.id}`); toast.success("Meetup cancelled"); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="mt-5" data-testid="community-meetups">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="font-serif text-xl flex items-center gap-2"><CalendarDays size={18} className="text-brand" /> Meetups</h4>
          <p className="text-xs text-sec">Schedule online (Secure Meet) or offline gatherings and RSVP.</p>
        </div>
        {canParticipate && (
          <button data-testid="meetup-add-toggle" onClick={() => setShowAdd((s) => !s)} className="btn-primary h-9 px-4 flex items-center gap-1.5 text-sm shrink-0"><Plus size={15} /> Schedule meetup</button>
        )}
      </div>

      <AnimatePresence>
        {showAdd && canParticipate && (
          <CreateMeetup communityId={community.id} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />
        )}
      </AnimatePresence>

      {loading ? (
        <div className="text-center text-sec py-4"><HeartLoader /></div>
      ) : meetups.length === 0 ? (
        <div className="text-sm text-sec rud-card rounded-xl p-4" data-testid="meetups-empty">No meetups scheduled yet.{canParticipate ? " Be the first to host one!" : ""}</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="meetups-list">
          {meetups.map((m) => (
            <div key={m.id} className="rud-card rounded-2xl p-4" data-testid={`meetup-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {m.mode === "online" ? <Video size={15} className="text-sky-400 shrink-0" /> : <MapPin size={15} className="text-emerald-400 shrink-0" />}
                    <span className="truncate">{m.title}</span>
                  </div>
                  {m.description && <p className="text-xs text-sec mt-1 line-clamp-2">{m.description}</p>}
                </div>
                {m.is_organizer && (
                  <button data-testid={`meetup-delete-${m.id}`} onClick={() => remove(m)} disabled={busy === m.id} className="text-rose-400 hover:opacity-80 shrink-0" title="Cancel meetup"><Trash2 size={15} /></button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-sec mt-2">
                <span className="uppercase tracking-wide text-brand">{m.mode}</span>
                {m.when && <span className="flex items-center gap-1"><Clock size={12} /> {m.when}</span>}
                {m.mode === "offline" && m.location && <span className="flex items-center gap-1"><MapPin size={12} /> {m.location}</span>}
                <span className="flex items-center gap-1"><Users size={12} /> {m.rsvp_count} going{m.capacity ? ` / ${m.capacity}` : ""}</span>
                <span>· by {m.organizer_name}</span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button data-testid={`meetup-rsvp-${m.id}`} onClick={() => rsvp(m)} disabled={busy === m.id || (!m.is_going && m.full)}
                  className={`h-9 px-4 rounded-full text-sm flex items-center gap-1.5 border transition-colors ${m.is_going ? "border-brand text-brand bg-brand/10" : m.full ? "border-rud text-sec opacity-60 cursor-not-allowed" : "border-rud text-sec hover:border-brand"}`}>
                  {m.is_going ? <><Check size={14} /> Going</> : m.full ? "Full" : "RSVP"}
                </button>
                {m.mode === "online" && (m.is_going || m.is_organizer) && (
                  <button data-testid={`meetup-join-${m.id}`} onClick={() => openMeet(m)} className="btn-primary h-9 px-4 rounded-full text-sm flex items-center gap-1.5"><Video size={14} /> Join</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateMeetup({ communityId, onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", mode: "online", when: "", location: "", link: "", capacity: 0 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Give your meetup a title."); return; }
    setBusy(true);
    try {
      await api.post(`/communities/${communityId}/meetups`, { ...f, capacity: Number(f.capacity) || 0 });
      toast.success("Meetup scheduled!");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
      <div className="rud-card rounded-2xl p-5 mb-3 space-y-3" data-testid="create-meetup-form">
        <div className="flex items-center justify-between">
          <div className="font-medium flex items-center gap-2"><CalendarDays size={16} className="text-brand" /> Schedule a meetup</div>
          <button onClick={onClose} className="text-sec hover:text-[var(--rud-text)]"><X size={16} /></button>
        </div>
        <div className="flex gap-2">
          {[["online", Video, "Online (Secure Meet)"], ["offline", MapPin, "Offline"]].map(([m, Icon, label]) => (
            <button key={m} type="button" data-testid={`meetup-mode-${m}`} onClick={() => setF((p) => ({ ...p, mode: m }))}
              className={`h-9 px-4 rounded-full text-sm flex items-center gap-1.5 ${f.mode === m ? "btn-primary" : "btn-ghost"}`}><Icon size={14} /> {label}</button>
          ))}
        </div>
        <input data-testid="meetup-title" value={f.title} onChange={set("title")} placeholder="Meetup title" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
        <textarea data-testid="meetup-description" value={f.description} onChange={set("description")} rows={2} placeholder="What's this meetup about?" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-none" />
        <input data-testid="meetup-when" value={f.when} onChange={set("when")} placeholder="When? (e.g. Sat 24 Aug, 6 PM IST)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
        {f.mode === "offline" ? (
          <input data-testid="meetup-location" value={f.location} onChange={set("location")} placeholder="Location / address" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
        ) : (
          <input data-testid="meetup-link" value={f.link} onChange={set("link")} placeholder="Meeting link (optional — leave blank for a Secure Meet room)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
        )}
        <div className="flex items-center gap-2">
          <input data-testid="meetup-capacity" type="number" min="0" value={f.capacity} onChange={set("capacity")} className="w-28 rud-input rounded-lg px-3 py-2.5 text-sm" />
          <span className="text-xs text-sec">max attendees (0 = unlimited)</span>
        </div>
        <button data-testid="meetup-create-submit" onClick={submit} disabled={busy} className="btn-primary w-full h-11 flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <CalendarDays size={16} />} Schedule meetup
        </button>
      </div>
    </motion.div>
  );
}

function CreateCommunity({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", interest: "", description: "", category: "Interest", join_mode: "open" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const JOIN_MODES = [
    ["open", "Open", "Anyone can join instantly"],
    ["approve", "Approve", "You approve each member"],
    ["follow", "Follow-only", "People follow, no membership"],
  ];

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name your community."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/communities", f);
      toast.success("Community created!");
      onDone(data.community);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/60" onClick={onClose}>
      <motion.div initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} className="rud-card rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="create-community-form">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl flex items-center gap-2"><Sparkles size={18} className="text-brand" /> Create your community</h3>
          <button onClick={onClose} className="text-sec hover:text-[var(--rud-text)]"><X size={18} /></button>
        </div>
        <p className="text-xs text-sec mb-4">Bring people together around a shared interest, place, topic or cause. You can create <b className="text-[var(--rud-text)]">one</b> community.</p>
        <div className="space-y-3">
          <input data-testid="community-name" value={f.name} onChange={set("name")} placeholder="Community name (e.g. Sunrise Runners)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <div>
            <label className="text-[11px] uppercase tracking-widest text-sec block mb-1.5">Category</label>
            <div className="flex flex-wrap gap-1.5" data-testid="community-category-picker">
              {COMMUNITY_CATS.filter((c) => c !== "all").map((c) => (
                <button key={c} type="button" data-testid={`community-cat-opt-${c.toLowerCase()}`} onClick={() => setF((prev) => ({ ...prev, category: c }))}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.category === c ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand/60"}`}>{c}</button>
              ))}
            </div>
          </div>
          <input data-testid="community-interest" value={f.interest} onChange={set("interest")} placeholder="Interest / topic tag (e.g. running, art, elections, Mumbai)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <div>
            <label className="text-[11px] uppercase tracking-widest text-sec block mb-1.5">Who can join?</label>
            <div className="grid grid-cols-3 gap-2" data-testid="community-joinmode-picker">
              {JOIN_MODES.map(([id, label, desc]) => (
                <button key={id} type="button" data-testid={`joinmode-${id}`} onClick={() => setF((prev) => ({ ...prev, join_mode: id }))}
                  className={`rounded-xl border p-2.5 text-left transition-colors ${f.join_mode === id ? "bg-brand/10 border-brand" : "border-rud hover:border-brand/50"}`}>
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-[10px] text-sec leading-tight mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
          </div>
          <textarea data-testid="community-description" value={f.description} onChange={set("description")} rows={4} placeholder="What is this community about? Who is it for?" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-none" />
          <button data-testid="community-create-submit" onClick={submit} disabled={busy} className="btn-primary w-full h-11 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />} Create community
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
